import React from 'react';
import Reveal from '../components/Reveal';

const Privacy: React.FC = () => {
  return (
    <div className="pb-20">
      <section className="px-6 pt-20 pb-12 text-center md:pt-32">
        <h1 className="mx-auto max-w-3xl text-4xl font-semibold tracking-tight text-white md:text-6xl">
          Privacy.
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-lg text-white/50">
          Vizo is designed to keep music files and creative work primarily on your device.
        </p>
        <p className="mt-2 text-sm text-white/40">Last updated: May 30, 2026</p>
      </section>

      <section className="px-6 py-8">
        <div className="apple-section">
          <div className="grid gap-4 md:grid-cols-2">
            <Reveal>
              <div className="apple-card">
                <h2 className="text-2xl font-semibold text-white">What Vizo uses</h2>
                <p className="mt-3 text-sm text-white/50">
                  Vizo uses audio files, artwork, templates, presets, and export settings to generate music visualizations inside the app.
                  Most of this processing happens locally on your iPhone.
                </p>
                <ul className="mt-4 space-y-2.5">
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Imported audio and images are used to render visuals you create.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Export preferences are stored locally so your workflow stays consistent.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Subscription status is handled through Apple and StoreKit.</span>
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="apple-card">
                <h2 className="text-2xl font-semibold text-white">What we do not store</h2>
                <p className="mt-3 text-sm text-white/50">
                  We do not run a cloud media library for your imported songs, exported videos, or preset artwork through this website.
                </p>
                <ul className="mt-4 space-y-2.5">
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>No payment card data is stored by us.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>No uploaded audio library is maintained by this site.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Your App Store purchase flow remains under Apple systems.</span>
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal>
              <div className="apple-card">
                <h2 className="text-2xl font-semibold text-white">Diagnostics and support</h2>
                <p className="mt-3 text-sm text-white/50">
                  If you contact support, you may choose to share screenshots, device details, app version information, or issue descriptions.
                </p>
                <ul className="mt-4 space-y-2.5">
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Support emails are used only to respond to your request.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Crash or diagnostic data may be provided through Apple platforms.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>You can stop sharing new support information at any time.</span>
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="apple-card">
                <h2 className="text-2xl font-semibold text-white">Your choices</h2>
                <ul className="mt-4 space-y-2.5">
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Delete local app data by removing the app from your device.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Manage subscriptions from your Apple ID subscription settings.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Contact support if you need clarification about data use.</span>
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal>
              <div className="apple-card">
                <h2 className="text-2xl font-semibold text-white">Community sharing</h2>
                <p className="mt-3 text-sm text-white/50">
                  If you publish a preset or comment in the Vizo Community, that content is stored on our servers so other users can see it.
                </p>
                <ul className="mt-4 space-y-2.5">
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Sign in with Apple gives us only a stable, anonymous identifier; your name is optional.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Published presets, preview images, and comments are stored on our secure servers and shown to others.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Preview images are automatically screened; you can report content and block members in-app.</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                    <span>Email vizomusic@proton.me to delete your published presets and community data.</span>
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal>
            <div className="apple-card mt-4">
              <h2 className="text-2xl font-semibold text-white">Contact</h2>
              <p className="mt-3 text-sm text-white/50">
                Questions about privacy or support can be sent to the addresses below.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/70">
                <li>Email: vizomusic@proton.me</li>
                <li>Support: vizomusic@proton.me</li>
                <li>
                  Terms:{' '}
                  <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer" className="apple-link">
                    Apple Standard EULA
                  </a>
                </li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
